import PostModel from 'apps/posting/models/Post'
import { ListName, ListNameStatusMap } from 'apps/posting/models/Post/constants'
import { IPost } from 'apps/posting/postingInterfaces'
import { mapResponse } from 'shared/api/lib/mapResponse'
import { BroadcastingApi } from 'shared/api/requests/broadcasting'
import { createListSlice } from 'shared/lib/factory/redux/createListSlice'
import { createListOperations } from 'shared/lib/factory/redux/createListSlice/lib/createListOperations'

import { addPost, updatePost } from '../../actions/postActions'

type PostListApiMethod = Extract<
  keyof typeof BroadcastingApi,
  'loadDrafts' | 'loadScheduled' | 'loadHistory'
>

interface ExtraState {
  total: number | null
}

const extraState: ExtraState = {
  total: null,
}

interface PostListSliceFactoryOptions {
  listName: ListName
  apiMethod: PostListApiMethod
  namespace: string
  storePath: string
}

const createPostListSlice = ({
  listName,
  apiMethod,
  namespace,
  storePath,
}: PostListSliceFactoryOptions) => {
  const STATUSES = ListNameStatusMap[listName]

  type ApiArg = Parameters<(typeof BroadcastingApi)[PostListApiMethod]>[0]
  const mapListResponse = mapResponse(
    (arg: ApiArg) => {
      const { limiter_type, ...query } = (arg?.query ?? {}) as Record<string, unknown>
      return BroadcastingApi[apiMethod]({ ...arg, query: query as ApiArg['query'] })
    },
    (response) => ({
      ...response.data,
      posts: response.data.posts.map(PostModel.adapter),
      limiter: response.data.limiter as number | null,
    }),
  )

  return createListSlice({
    namespace,
    storePath,
    listKey: 'posts',

    operations: createListOperations({
      getList: mapListResponse,
      getNextList: mapListResponse,
      getPrevList: undefined,
      createItem: undefined,
      updateItem: undefined,
      deleteItem: undefined,
    }),

    getItemId: (item: IPost) => item.id,
    extraState,
    reducers: {},

    extraReducers: (builder) => {
      builder.addCase(addPost.fulfilled, (state, action) => {
        const newItem = action.payload
        if (!STATUSES.includes(newItem.status)) return

        const index = state.items.findIndex((item) => item.id === newItem.id)
        if (index !== -1) {
          Object.assign(state.byId[newItem.id], newItem)
          Object.assign(state.items[index], newItem)
        } else {
          state.items.push(newItem)
          state.byId[newItem.id] = newItem
          if (state.total !== null) state.total += 1
        }
      })

      builder.addCase(updatePost, (state, action) => {
        const { postId: rawPostId, changes } = action.payload
        const postId = Number(rawPostId)
        const wasHere = Boolean(state.byId[postId])
        const newStatus = changes.status
        const belongsNow = newStatus ? STATUSES.includes(newStatus) : wasHere

        if (wasHere && belongsNow) {
          Object.assign(state.byId[postId], changes)
          const index = state.items.findIndex((item) => item.id === postId)
          if (index !== -1) Object.assign(state.items[index], changes)
        } else if (wasHere && !belongsNow) {
          delete state.byId[postId]
          const index = state.items.findIndex((item) => item.id === postId)
          if (index !== -1) state.items.splice(index, 1)
          if (state.total !== null) state.total -= 1
        } else if (!wasHere && belongsNow && newStatus) {
          const fullItem = { id: postId, ...changes }
          state.items.push(fullItem)
          state.byId[postId] = fullItem
          if (state.total !== null) state.total += 1
        }
      })
    },
  })
}

export const {
  reducer: draftsReducer,
  actions: draftsActions,
  selectors: draftsSelectors,
  initialState: draftsInitialState,
} = createPostListSlice({
  listName: ListName.DRAFTS,
  apiMethod: 'loadDrafts',
  namespace: 'postDrafts',
  storePath: 'posts.drafts',
})

export const {
  reducer: scheduledReducer,
  actions: scheduledActions,
  selectors: scheduledSelectors,
  initialState: scheduledInitialState,
} = createPostListSlice({
  listName: ListName.SCHEDULED,
  apiMethod: 'loadScheduled',
  namespace: 'postScheduled',
  storePath: 'posts.scheduled',
})

export const {
  reducer: historyReducer,
  actions: historyActions,
  selectors: historySelectors,
  initialState: historyInitialState,
} = createPostListSlice({
  listName: ListName.HISTORY,
  apiMethod: 'loadHistory',
  namespace: 'postHistory',
  storePath: 'posts.history',
})
